import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Spline from '@splinetool/react-spline';
import { Sparkle, Download } from '@phosphor-icons/react';
import { useScrollContext } from '../context/ScrollContext';
import { usePortfolio } from '../context/PortfolioContext';
import '../styles/animations.css';
export function HeroSection() {
  const {
    highlightAllText
  } = useScrollContext();
  const {
    openChatDrawer,
    closeChatDrawer,
    setChatEntryPoint,
    isChatDrawerOpen
  } = usePortfolio();
  const location = useLocation();
  const navigate = useNavigate();
  const scrollToCaseStudies = () => {
    const caseStudiesSection = document.getElementById('projects');
    if (caseStudiesSection) {
      const isMobile = window.innerWidth < 768;
      const offset = isMobile ? 80 : 60;
      const elementPosition = caseStudiesSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset + offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const scrollToFeatured = () => {
    const featuredSection = document.getElementById('featured-work');
    if (featuredSection) {
      featuredSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const scrollToFooter = () => {
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleChatClick = () => {
    setChatEntryPoint(location.pathname);
    if (isChatDrawerOpen) {
      closeChatDrawer();
    } else {
      openChatDrawer();
      if (typeof window !== 'undefined' && (window as any).gtag) {
        ;
        (window as any).gtag('event', 'chat_opened', {
          event_category: 'Chat',
          event_label: 'Hero Button',
          source: 'hero_button'
        });
      }
    }
  };
  const BorderBeamButton = ({
    onClick,
    className,
    children
  }: {
    onClick: () => void;
    className?: string;
    children?: React.ReactNode;
  }) => {
    const [mousePos, setMousePos] = useState({
      x: 0,
      y: 0
    });
    const [isHovered, setIsHovered] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };
    return <div className={`relative group ${className || ''}`} data-id="element-1759">
        <button ref={buttonRef} onClick={onClick} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className="relative z-10 overflow-hidden transition-transform duration-150 ease-out active:scale-[0.98] text-white bg-neutral-900/60 border-white/20 border rounded-xl pt-3 pr-6 pb-3 pl-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] w-full" style={{
        '--x': `${mousePos.x}px`,
        '--y': `${mousePos.y}px`,
        '--o': isHovered ? '1' : '0'
      } as React.CSSProperties} data-id="element-1760">
          <span className="relative z-10 inline-flex items-center gap-2 font-semibold" data-id="element-1761">
            {children || 'View Case Studies'}
          </span>

          <span className="pointer-events-none absolute bottom-0 left-1/2 right-1/2 h-px bg-gradient-to-r from-transparent via-white to-transparent opacity-80 transition-[left,right] duration-500 ease-out group-hover:left-0 group-hover:right-0" data-id="element-1762"></span>

          <span className="glow pointer-events-none absolute inset-0 -z-10" aria-hidden="true" style={{
          transform: 'scale(0.95) translate(0px, -24px)'
        }} data-id="element-1763"></span>
        </button>

        <span className="pointer-events-none absolute -bottom-3 left-1/2 z-0 h-6 w-44 -translate-x-1/2 rounded-full opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100" style={{
        background: 'radial-gradient(60% 100% at 50% 50%, rgba(255,255,255,.55), rgba(255,255,255,.28) 35%, transparent 70%)',
        filter: 'blur(10px) saturate(120%)'
      }} aria-hidden="true" data-id="element-1764"></span>
      </div>;
  };
  return <section className="relative w-full flex items-center justify-center hero-section" id="home" aria-label="Hero section" style={{
    backgroundColor: '#000000',
    overflow: 'hidden'
  }} data-id="element-1765">
      {/* Spline 3D Background - Lowest Layer (Z-Index 1) */}
      <div className="absolute inset-0 pointer-events-none" style={{
      zIndex: 1,
      backgroundColor: '#000000'
    }} data-id="element-1766">
        <Spline scene="https://prod.spline.design/hhVQXtsLw7Tkkv-l/scene.splinecode" style={{
        width: '100%',
        height: '100%'
      }} data-id="element-1767" />
      </div>

      {/* Mobile Text Overlay - Shows on mobile only */}
      <div className="absolute md:hidden pointer-events-none px-6" style={{
      top: '50%',
      transform: 'translateY(calc(-50% - 40px))',
      left: 0,
      right: 0,
      zIndex: 15
    }} data-id="element-1768">
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'flex-start'
      }} data-id="element-1769">
          <h1 className="animate-slide-up" style={{
          fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
          fontWeight: 700,
          color: '#FFFFFF',
          fontFamily: "'Gefika', sans-serif",
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
          margin: 0,
          textTransform: 'uppercase'
        }} data-id="element-1770">
            Hi, I'm Mateo
          </h1>

          <div className="animate-slide-up stagger-1" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }} data-id="element-1771">
            <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            pointerEvents: 'auto'
          }} data-id="element-1772">
              <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.01em'
            }} data-id="element-1773">
                Lead Product Designer
              </span>
              <a href="https://docs.google.com/document/d/1XmUDOfp7By_54KFbz9PI8GnRCbHTbtUHlIfyEodo_hE/edit?usp=sharing" target="_blank" rel="noopener noreferrer" className="nav-link" aria-label="Download resume" style={{
              fontSize: '0.7rem',
              color: 'rgba(255, 255, 255, 0.6)',
              fontFamily: "'Ronzino', sans-serif",
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem 0',
              transition: 'all 0.3s ease',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              textDecoration: 'none',
              minHeight: '44px'
            }} data-id="element-1774">
                <Download size={11} weight="regular" aria-hidden="true" data-id="element-1775" />
                <span data-id="element-1776">Resume</span>
              </a>
            </div>
            <p style={{
            fontSize: '16px',
            fontWeight: '400',
            color: 'rgba(255, 255, 255, 0.85)',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            letterSpacing: '0.01em',
            lineHeight: '1.6',
            margin: 0,
            maxWidth: '90%'
          }} data-id="element-1777">
              I design B2B SaaS products for enterprise clients in finance, AI,
              and cybersecurity. I've led design teams and now occasionally
              advise AI startups on confidential projects behind the scenes.
            </p>
          </div>
        </div>
      </div>

      {/* UI Container - Top Layer (Z-Index 20) */}
      <div className="absolute inset-0 pointer-events-none px-6 sm:px-12 md:px-16 lg:px-24" style={{
      zIndex: 20
    }} data-id="element-1778">
        <div className="w-full max-w-7xl mx-auto h-full relative" data-id="element-1779">
          <div className="absolute hidden md:flex flex-col" style={{
          top: '50%',
          transform: 'translateY(-50%)',
          left: '40px',
          gap: '24px',
          zIndex: 20,
          maxWidth: '500px',
          alignItems: 'flex-start'
        }} data-id="element-1780">
            <h1 className="animate-slide-up" style={{
            fontSize: 'clamp(3.25rem, 5.5vw, 5rem)',
            fontWeight: 700,
            color: '#FFFFFF',
            fontFamily: "'Gefika', sans-serif",
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap'
          }} data-id="element-1781">
              Hi, I'm Mateo
            </h1>

            <div className="animate-slide-up stagger-1" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }} data-id="element-1782">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              pointerEvents: 'auto'
            }} data-id="element-1783">
                <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                letterSpacing: '0.01em'
              }} data-id="element-1784">
                  Lead Product Designer
                </span>
                <a href="https://docs.google.com/document/d/1XmUDOfp7By_54KFbz9PI8GnRCbHTbtUHlIfyEodo_hE/edit?usp=sharing" target="_blank" rel="noopener noreferrer" className="nav-link" aria-label="Download resume" style={{
                fontSize: '0.7rem',
                color: 'rgba(255, 255, 255, 0.6)',
                fontFamily: "'Ronzino', sans-serif",
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem 0',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                textDecoration: 'none'
              }} data-id="element-1785">
                  <Download size={11} weight="regular" aria-hidden="true" data-id="element-1786" />
                  <span data-id="element-1787">Resume</span>
                </a>
              </div>
              <p style={{
              fontSize: '18px',
              fontWeight: '400',
              color: 'rgba(255, 255, 255, 0.85)',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              letterSpacing: '0.01em',
              lineHeight: '1.6',
              margin: 0,
              maxWidth: '90%'
            }} data-id="element-1788">
                I design B2B SaaS products for enterprise clients in finance,
                AI, and cybersecurity. I've led design teams and now
                occasionally advise AI startups on confidential projects behind
                the scenes.
              </p>
            </div>

            <img className="animate-slide-up stagger-2" src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Signature.png" alt="Mateo Cohen's signature" style={{
            width: 'clamp(60px, 9vw, 110px)',
            height: 'auto',
            opacity: 0.8,
            marginTop: '-8px'
          }} data-id="element-1789" />

            {/* CTA Buttons */}
            <div className="animate-slide-up stagger-3" style={{
            display: 'flex',
            gap: '16px',
            marginTop: '8px',
            pointerEvents: 'auto'
          }} data-id="element-1790">
              <BorderBeamButton onClick={scrollToCaseStudies} data-id="element-1791">
                My Case Studies
              </BorderBeamButton>

              <button onClick={handleChatClick} className="nav-link" aria-label="Open chat" style={{
              fontSize: '0.95rem',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: "'Ronzino', sans-serif",
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              cursor: 'pointer',
              padding: '0.75rem 1.5rem',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }} data-id="element-1792">
                <span data-id="element-1793">AI Chat</span>
                <Sparkle size={16} weight="regular" aria-hidden="true" data-id="element-1794" />
              </button>
            </div>
          </div>

          <nav className="absolute hidden md:block pointer-events-auto" aria-label="Page navigation" style={{
          right: '40px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 20
        }} data-id="element-1795"></nav>

          <div data-mobile-buttons="true" className="absolute md:hidden bottom-10 left-0 right-0 pointer-events-auto animate-slide-up stagger-2" style={{
          zIndex: 20
        }} data-id="element-1796">
            <div className="flex flex-col items-stretch gap-4 w-full" data-id="element-1797">
              <BorderBeamButton onClick={scrollToCaseStudies} className="w-full" data-id="element-1798">
                View Case Studies
              </BorderBeamButton>
              <button onClick={handleChatClick} className="nav-link w-full" aria-label="Open chat" style={{
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: "'Ronzino', sans-serif",
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              cursor: 'pointer',
              textAlign: 'center',
              padding: '0.75rem 1rem',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap',
              minHeight: '44px'
            }} data-id="element-1799">
                <span data-id="element-1800">AI Chat</span>
                <Sparkle size={14} weight="regular" aria-hidden="true" data-id="element-1801" />
              </button>
            </div>
          </div>

          <div className="absolute left-0 right-0 bottom-20 pointer-events-none hidden md:block" style={{
          zIndex: 20
        }} data-id="element-1802"></div>
        </div>
      </div>

      <style data-id="element-1803">{`
        @keyframes beamV {
          0% {
            top: -150px;
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 0;
          }
        }
        
        /* CRITICAL: Ensure hero section covers full viewport on mobile */
        .hero-section { 
          min-height: 100vh; 
          height: 100vh;
          background-color: #000000;
        }
        
        /* Use stable viewport height on mobile */
        @supports (height: 100svh) {
          @media (max-width: 768px) { 
            .hero-section { 
              min-height: 100svh; 
              height: 100svh;
            } 
          }
        }
        
        /* Fallback for browsers without svh support */
        @supports not (height: 100svh) {
          @media (max-width: 768px) {
            .hero-section {
              min-height: 100vh;
              height: 100vh;
            }
          }
        }
        
        .mobile-info {
          display: none;
        }
        
        @media (max-width: 768px) {
          .mobile-info {
            display: block;
          }
        }
        
        @media (min-width: 768px) {
          [data-mobile-buttons="true"] {
            display: none !important;
          }
        }
        
        .hero-highlight {
          background: linear-gradient(135deg, #00ff88 0%, #00ffcc 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 500;
          font-family: 'Gefika', sans-serif;
        }
        
        .nav-link { position: relative; }
        .nav-link:hover { color: rgba(255, 255, 255, 1) !important; }
        .nav-link svg { transition: transform 0.3s ease, color 0.3s ease; }
        .nav-link:hover svg { transform: translateY(3px); color: rgba(255, 255, 255, 1); }
        @media (max-width: 768px) { 
          .nav-link { color: rgba(255, 255, 255, 0.9) !important; }
        }
        .tagline-text-container { top: calc(35vh - 136px); }
        .tagline-text { font-size: clamp(2rem, 4.5vw, 4rem); }
        .chat-open .tagline-text { font-size: clamp(2rem, 4vw, 3.75rem); }
        .unicorn-canvas-container { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .unicorn-canvas-wrapper { position: relative; width: 100%; height: 100%; min-width: 177.78vh; min-height: 56.25vw; }
        [data-us-project-src] { width: 100% !important; height: 100% !important; }
        [data-us-project-src] canvas { width: 100% !important; height: 100% !important; }
        
        @media (max-width: 768px) {
          .md\\:\\!transform-none {
            transform: scale(1.8) !important;
          }
        }
        
        @media (max-width: 1024px) { .tagline-text-container { top: 22vh; } .tagline-text { font-size: clamp(1.6rem, 4.5vw, 3.2rem); } }
        @media (max-width: 768px) {
          .tagline-text-container { top: 12vh !important; z-index: 15; gap: 0.75rem; }
          .tagline-text { font-size: clamp(1.85rem, 6vw, 2.75rem); font-weight: 600; line-height: 1.2; }
          .tagline-text span { white-space: nowrap; }
          .unicorn-canvas-container { align-items: flex-start; padding-top: 0; margin-top: -5svh; clip-path: inset(28% 0 0 0); }
        }
        @media (max-width: 480px) {
          .tagline-text-container { top: 10vh !important; z-index: 15; gap: 0.5rem; }
          .tagline-text { font-size: clamp(1.6rem, 7vw, 2.4rem); font-weight: 600; line-height: 1.2; }
          .tagline-text span { white-space: nowrap; }
          .unicorn-canvas-container { margin-top: -7svh; clip-path: inset(30% 0 0 0); }
        }
        @media (max-width: 360px) {
          .tagline-text { font-size: clamp(1.4rem, 6.5vw, 1.9rem); }
        }
        @media (min-width: 1920px) { .tagline-text-container { top: 15vh; } }
        @media (max-width: 768px) {
          .hero-name-text {
            font-size: clamp(6rem, 20vw, 11rem) !important;
            top: 8vh !important;
          }
        }
        @media (max-width: 480px) {
          .hero-name-text {
            font-size: clamp(5rem, 18vw, 9rem) !important;
            top: 6vh !important;
          }
        }
      `}</style>
    </section>;
}